import React, { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ConverterPage } from './pages/ConverterPage';
import { PrivacyPolicy } from './pages/privacyPolicy';
import { TermsOfUse } from './pages/termsOfUse';
import { CourseBuilderPage } from './pages/CourseBuilderPage';
import i18n from './i18n';
import { schedulePageView } from './utils/analytics';
import { CookieConsent } from './components/ui/cookie-consent';

const SUPPORTED_LANGS = ['en', 'uk', 'es', 'fr', 'pl', 'it', 'pt'];

// Language wrapper component that sets language based on URL
const LanguageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  const supported = Boolean(lang && SUPPORTED_LANGS.includes(lang));

  useEffect(() => {
    if (supported && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, supported]);

  // One GA4 page_view per real page. Only matched content routes render this wrapper,
  // so redirect-only URLs ("/", "/ar/privacy-policy", "/en/unknown") are never counted —
  // otherwise one visit would be reported as two pages.
  // The timing of the hit (one frame, or at once in a background tab) is in
  // schedulePageView.
  useEffect(() => {
    if (!supported) return;
    return schedulePageView();
  }, [supported, location.pathname, location.search]);

  // Unsupported or retired language (e.g. /ar/privacy-policy): same page in English.
  if (lang && !SUPPORTED_LANGS.includes(lang)) {
    const rest = location.pathname.slice(lang.length + 1) || '/';
    return <Navigate to={`/en${rest}${location.search}${location.hash}`} replace />;
  }

  return <>{children}</>;
};

// Redirect component that preserves path and redirects to language version
const RedirectToLanguage: React.FC<{ to: string }> = ({ to }) => {
  const savedLang = localStorage.getItem('i18nextLng');
  const browserLang = navigator.language.split('-')[0];
  const defaultLang = savedLang && SUPPORTED_LANGS.includes(savedLang)
    ? savedLang
    : (SUPPORTED_LANGS.includes(browserLang) ? browserLang : 'en');

  return <Navigate to={`/${defaultLang}${to}`} replace />;
};

function App() {
  return (
    <React.StrictMode>
      <HelmetProvider>
        <Router>
          <Routes>
            {/* Root redirects to detected/default language */}
            <Route path="/" element={<RedirectToLanguage to="/" />} />

            {/* Language-prefixed routes */}
            <Route path="/:lang" element={<LanguageWrapper><ConverterPage /></LanguageWrapper>} />
            <Route path="/:lang/" element={<LanguageWrapper><ConverterPage /></LanguageWrapper>} />
            <Route path="/:lang/privacy-policy" element={<LanguageWrapper><PrivacyPolicy /></LanguageWrapper>} />
            <Route path="/:lang/terms-of-use" element={<LanguageWrapper><TermsOfUse /></LanguageWrapper>} />
            <Route path="/:lang/ai-course-builder" element={<LanguageWrapper><CourseBuilderPage /></LanguageWrapper>} />

            {/* Fallback for old URLs without language prefix - redirect to default language */}
            <Route path="/privacy-policy" element={<RedirectToLanguage to="/privacy-policy" />} />
            <Route path="/terms-of-use" element={<RedirectToLanguage to="/terms-of-use" />} />
            <Route path="/ai-course-builder" element={<RedirectToLanguage to="/ai-course-builder" />} />

            {/* Catch-all redirect */}
            <Route path="*" element={<RedirectToLanguage to="/" />} />
          </Routes>
          <CookieConsent />
        </Router>
      </HelmetProvider>
    </React.StrictMode>
  );
}

export default App;
